import { useState } from 'react';
import type { Datacenter } from '../types';
import { mockDatacenterServers } from '../mockData';

interface DatacenterDetailModalProps {
  datacenter: Datacenter;
  onClose: () => void;
  onEdit?: () => void;
}

export default function DatacenterDetailModal({ datacenter, onClose, onEdit }: DatacenterDetailModalProps) {
  const [servers, setServers] = useState(mockDatacenterServers);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefreshServers = () => {
    setIsRefreshing(true);
    // 模拟刷新服务器列表
    setTimeout(() => {
      setIsRefreshing(false);
      alert('服务器列表已刷新');
    }, 1000);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">机房详情</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-6">
          {/* 基础信息 */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">基础信息</h3>
              {onEdit && (
                <button
                  onClick={onEdit}
                  className="px-3 py-1.5 text-sm font-medium text-teal-700 bg-teal-50 rounded-lg hover:bg-teal-100 transition-colors whitespace-nowrap"
                >
                  <i className="ri-edit-line mr-1"></i>
                  编辑
                </button>
              )}
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">机房ID</div>
                  <div className="text-sm text-gray-900">{datacenter.id}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">机房名称</div>
                  <div className="text-sm text-gray-900">{datacenter.name}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">机房代码</div>
                  <div className="text-sm text-gray-900">{datacenter.code}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">行政区域</div>
                  <div className="text-sm text-gray-900">
                    {datacenter.location.province} {datacenter.location.city} {datacenter.location.district}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-xs text-gray-500 mb-1">详细地址</div>
                  <div className="text-sm text-gray-900">{datacenter.location.address}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">机房类型</div>
                  <div className="text-sm text-gray-900">
                    {datacenter.type === 'self-built' ? '自建' : datacenter.type === 'hosted' ? '托管' : '租用'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">网络出口带宽</div>
                  <div className="text-sm text-gray-900">{datacenter.bandwidth}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">运营商</div>
                  <div className="text-sm text-gray-900">{datacenter.isp}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">建设日期</div>
                  <div className="text-sm text-gray-900">{datacenter.buildDate}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">联系人</div>
                  <div className="text-sm text-gray-900">{datacenter.contact.name}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">联系电话</div>
                  <div className="text-sm text-gray-900">{datacenter.contact.phone}</div>
                </div>
                {datacenter.description && (
                  <div className="col-span-3">
                    <div className="text-xs text-gray-500 mb-1">机房描述</div>
                    <div className="text-sm text-gray-900">{datacenter.description}</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 资源监控 */}
          <div className="mb-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">资源监控</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="text-xs text-gray-500 mb-2">出口带宽</div>
                <div className="flex items-end justify-between mb-2">
                  <div className="text-2xl font-semibold text-gray-900">
                    {datacenter.monitoring.bandwidth.rate}%
                  </div>
                  <div className="text-xs text-gray-500">
                    {datacenter.monitoring.bandwidth.used}Gbps / {datacenter.monitoring.bandwidth.total}Gbps
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-teal-500 h-2 rounded-full transition-all"
                    style={{ width: `${datacenter.monitoring.bandwidth.rate}%` }}
                  ></div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="text-xs text-gray-500 mb-2">实例在线率</div>
                <div className="flex items-end justify-between mb-2">
                  <div className="text-2xl font-semibold text-gray-900">
                    {datacenter.monitoring.instanceOnlineRate.rate}%
                  </div>
                  <div className="text-xs text-gray-500">
                    {datacenter.monitoring.instanceOnlineRate.online} / {datacenter.monitoring.instanceOnlineRate.total}
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all"
                    style={{ width: `${datacenter.monitoring.instanceOnlineRate.rate}%` }}
                  ></div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="text-xs text-gray-500 mb-2">CPU 使用率</div>
                <div className="flex items-end justify-between mb-2">
                  <div className="text-2xl font-semibold text-gray-900">
                    {datacenter.monitoring.cpuUsage.rate}%
                  </div>
                  <div className="text-xs text-gray-500">
                    {datacenter.monitoring.cpuUsage.used} / {datacenter.monitoring.cpuUsage.total}核
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all"
                    style={{ width: `${datacenter.monitoring.cpuUsage.rate}%` }}
                  ></div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="text-xs text-gray-500 mb-2">GPU 使用率</div>
                <div className="flex items-end justify-between mb-2">
                  <div className="text-2xl font-semibold text-gray-900">
                    {datacenter.monitoring.gpuUsage.rate}%
                  </div>
                  <div className="text-xs text-gray-500">
                    {datacenter.monitoring.gpuUsage.used} / {datacenter.monitoring.gpuUsage.total}张
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-purple-500 h-2 rounded-full transition-all"
                    style={{ width: `${datacenter.monitoring.gpuUsage.rate}%` }}
                  ></div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="text-xs text-gray-500 mb-2">内存使用率</div>
                <div className="flex items-end justify-between mb-2">
                  <div className="text-2xl font-semibold text-gray-900">
                    {datacenter.monitoring.memoryUsage.rate}%
                  </div>
                  <div className="text-xs text-gray-500">
                    {datacenter.monitoring.memoryUsage.used}GB / {datacenter.monitoring.memoryUsage.total}GB
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-orange-500 h-2 rounded-full transition-all"
                    style={{ width: `${datacenter.monitoring.memoryUsage.rate}%` }}
                  ></div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="text-xs text-gray-500 mb-2">磁盘使用率</div>
                <div className="flex items-end justify-between mb-2">
                  <div className="text-2xl font-semibold text-gray-900">
                    {datacenter.monitoring.diskUsage.rate}%
                  </div>
                  <div className="text-xs text-gray-500">
                    {datacenter.monitoring.diskUsage.used}GB / {datacenter.monitoring.diskUsage.total}GB
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-pink-500 h-2 rounded-full transition-all"
                    style={{ width: `${datacenter.monitoring.diskUsage.rate}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* 服务器列表 */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">服务器列表</h3>
              <button
                onClick={handleRefreshServers}
                disabled={isRefreshing}
                className="px-3 py-1.5 text-sm font-medium text-teal-700 bg-teal-50 rounded-lg hover:bg-teal-100 transition-colors whitespace-nowrap disabled:opacity-50"
              >
                <i className={`ri-refresh-line mr-1 ${isRefreshing ? 'animate-spin' : ''}`}></i>
                {isRefreshing ? '刷新中...' : '刷新列表'}
              </button>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">服务器信息</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">内网IP</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">公网IP</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">硬件配置</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">状态</th>
                    </tr>
                  </thead>
                  <tbody>
                    {servers.map((server) => (
                      <tr key={server.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">
                          <div className="font-medium">{server.name}</div>
                          <div className="text-xs text-gray-500">{server.serverNumber}</div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">{server.internalIp}</td>
                        <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">{server.publicIp}</td>
                        <td className="py-3 px-4 text-sm text-gray-900">
                          <div className="space-y-1">
                            <div className="text-xs">CPU: {server.hardware.cpu}</div>
                            <div className="text-xs">内存: {server.hardware.memory}</div>
                            <div className="text-xs">硬盘: {server.hardware.disk}</div>
                            <div className="text-xs">显卡: {server.hardware.gpu}</div>
                          </div>
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                            server.status === 'normal' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {server.status === 'normal' ? '正常' : '停用'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
